import { Controller } from "@hotwired/stimulus";
import { Editor, type JSONContent } from "@tiptap/core";
import type { Level } from "@tiptap/extension-heading";
import type { Node as ProseNode } from "@tiptap/pm/model";
import { NodeSelection } from "@tiptap/pm/state";
import { createManager } from "../chapter_markdown";
import { atomBar } from "../chapter_editor/atom_bar";
import type { EditorContext, Reference } from "../chapter_editor/context";
import { belowOf, type MenuGroup, openFloating, openMenu } from "../chapter_editor/floating";
import { insertGroups } from "../chapter_editor/insert_menu";
import type { PickerContext } from "../chapter_editor/pickers";
import { ATOMS, editorExtensions, guardTests } from "../chapter_editor/node_views";
import { Nearby } from "../chapter_editor/nearby";
import { Previews } from "../chapter_editor/previews";
import { type TableBar, tableBar } from "../chapter_editor/table_bar";
import { tableAround } from "../chapter_markdown/table_nodes";

const HEADINGS: Level[] = [2, 3, 4];
const ATOM_BAR = "absolute z-10 flex items-center gap-2 rounded border border-line bg-surface-raised px-2 py-0.5 text-sm shadow";

// The chapter form's content field, edited as a document. The textarea stays the form field.
//
// A chapter nobody edits saves the text it was loaded with, whatever the serializer would write
// for it. Only a change made in the document (`touched`) puts the serializer's text in the field,
// and text typed in the source view is submitted as typed.
export default class extends Controller<HTMLFormElement> {
	static targets = [
		"field",
		"host",
		"area",
		"notice",
		"sections",
		"rail",
		"panel",
		"panelTitle",
		"panelBody",
		"saved",
		"unsaved",
		"blockType",
		"tool",
		"sourceButton",
		"visual",
	];
	static values = {
		previewUrl: String,
		referencesUrl: String,
		// The book and the saved chapter being edited, for the pictures and maps a search lists.
		book: String,
		chapter: String,
		monsterFormats: Array,
		openPaths: Object,
		labels: Object,
	};

	declare readonly fieldTarget: HTMLTextAreaElement;
	declare readonly hostTarget: HTMLElement;
	declare readonly areaTarget: HTMLElement;
	declare readonly hasNoticeTarget: boolean;
	declare readonly noticeTarget: HTMLElement;
	declare readonly hasSectionsTarget: boolean;
	declare readonly sectionsTarget: HTMLElement;
	declare readonly hasPanelTarget: boolean;
	declare readonly panelTarget: HTMLElement;
	declare readonly panelTitleTarget: HTMLElement;
	declare readonly panelBodyTarget: HTMLElement;
	declare readonly hasSavedTarget: boolean;
	declare readonly savedTarget: HTMLElement;
	declare readonly unsavedTarget: HTMLElement;
	declare readonly hasBlockTypeTarget: boolean;
	declare readonly blockTypeTarget: HTMLElement;
	declare readonly toolTargets: HTMLElement[];
	declare readonly hasSourceButtonTarget: boolean;
	declare readonly sourceButtonTarget: HTMLElement;
	declare readonly visualTargets: HTMLElement[];
	declare readonly railTargets: HTMLElement[];
	declare readonly previewUrlValue: string;
	declare readonly referencesUrlValue: string;
	declare readonly bookValue: string;
	declare readonly chapterValue: string;
	declare readonly monsterFormatsValue: string[];
	declare readonly openPathsValue: Record<string, string>;
	declare readonly labelsValue: Record<string, string>;

	editor?: Editor;
	// The text the field held when the chapter was opened, or when the source view last handed it back.
	original = "";
	// What the field held when the source view opened, to tell whether it was typed in.
	shown = "";
	touched = false;
	// Anything in the form changed since it opened, for the prompt before leaving.
	modified = false;
	submitting = false;
	inSource = false;
	manager = createManager();
	previews?: Previews;
	// Atoms are previewed as they come near the viewport while the editor is first drawn, and at once after.
	nearby = new Nearby();
	drawing = false;
	pieceBar?: HTMLElement;
	atomFor?: { from: number; node: ProseNode };
	atomResize?: ResizeObserver;
	tables?: { bar: TableBar; dom: HTMLElement; resize?: ResizeObserver };
	closeFloating?: () => void;

	connect() {
		this.original = this.fieldTarget.value;
		this.previews = new Previews(this.previewUrlValue);
		this.element.addEventListener("input", this.#changed);
		this.element.addEventListener("submit", this.#submit);
		this.element.addEventListener("direct-upload:error", this.#uploadFailed);
		document.addEventListener("submit", this.#submitted);
		window.addEventListener("beforeunload", this.#beforeUnload);
		document.addEventListener("turbo:before-visit", this.#beforeVisit);
		// A page always opens in the visual editor, whatever state the markup was left in.
		this.show(false);
		if (!this.load(this.original)) this.show(true);
	}

	disconnect() {
		this.#teardown();
		this.previews?.stop();
		this.element.removeEventListener("input", this.#changed);
		this.element.removeEventListener("submit", this.#submit);
		this.element.removeEventListener("direct-upload:error", this.#uploadFailed);
		document.removeEventListener("submit", this.#submitted);
		window.removeEventListener("beforeunload", this.#beforeUnload);
		document.removeEventListener("turbo:before-visit", this.#beforeVisit);
	}

	// Private on purpose: a Stimulus action can only name a public method, and nothing the page draws
	// (a preview is server HTML) may be able to make this controller write the field.
	#teardown() {
		this.closeFloating?.();
		this.closeFloating = undefined;
		this.#clearAtomBar();
		this.#clearTableBar();
		this.editor?.destroy();
		this.editor = undefined;
		this.nearby.stop();
		this.hostTarget.replaceChildren();
	}

	// Runs before the form's data is read; the only caller of #write besides the switch to the source view.
	#submit = (event: Event) => {
		if (!this.inSource && this.editor && this.touched && !this.#write()) {
			event.preventDefault();
			return;
		}
	};

	// Seen from the document, after Active Storage's own listener there: it cancels a submit to upload the
	// files first and submits again when they are up, and only a submit nobody cancelled leaves the page.
	#submitted = (event: Event) => {
		if (event.target === this.element && !event.defaultPrevented) this.submitting = true;
	};

	#uploadFailed = () => {
		this.submitting = false;
	};

	#beforeUnload = (event: BeforeUnloadEvent) => {
		if (!this.modified || this.submitting) return;
		event.preventDefault();
		event.returnValue = "";
	};

	#beforeVisit = (event: Event) => {
		if (this.modified && !this.submitting && !window.confirm(this.t("leave_confirm"))) event.preventDefault();
	};

	toggleSource() {
		if (!this.inSource) {
			if (this.editor && this.touched && !this.#write()) return;
			this.show(true);
			return;
		}
		if (!this.editor || this.fieldTarget.value !== this.shown) {
			if (!this.load(this.fieldTarget.value)) return;
			this.original = this.fieldTarget.value;
			this.touched = false;
		}
		this.notify();
		this.show(false);
		this.editor?.commands.focus();
	}

	bold() {
		this.editor?.chain().focus().toggleBold().run();
	}

	italic() {
		this.editor?.chain().focus().toggleItalic().run();
	}

	bulletList() {
		this.editor?.chain().focus().toggleBulletList().run();
	}

	orderedList() {
		this.editor?.chain().focus().toggleOrderedList().run();
	}

	undo() {
		this.editor?.chain().focus().undo().run();
	}

	redo() {
		this.editor?.chain().focus().redo().run();
	}

	openBlockMenu(event: Event) {
		const editor = this.editor;
		if (!editor) return;
		const run = (level: Level | 0) => () => (level ? editor.chain().focus().setHeading({ level }).run() : editor.chain().focus().setParagraph().run());
		const items: { level: Level | 0; label: string }[] = [
			{ level: 0, label: this.t("block_paragraph") },
			...HEADINGS.map((level) => ({ level, label: this.heading(level) })),
		];
		const current = editor.isActive("heading") ? Number(editor.getAttributes("heading").level) : 0;
		this.menu(belowOf(event.currentTarget as Element), [
			{ items: items.map((item) => ({ label: item.label, active: item.level === current, run: run(item.level) })) },
		]);
	}

	openInsertMenu(event: Event) {
		if (this.editor) this.menu(belowOf(event.currentTarget as Element), this.groups());
	}

	openLink(event: Event) {
		const editor = this.editor;
		if (!editor) return;
		const form = document.createElement("div");
		form.className = "flex flex-col gap-2 p-3";
		const input = document.createElement("input");
		input.className = "odo-form__input-text w-72 max-w-full";
		input.placeholder = this.t("link_url");
		input.setAttribute("aria-label", this.t("link_url"));
		input.value = String(editor.getAttributes("link").href ?? "");
		const actions = document.createElement("div");
		actions.className = "flex gap-2";
		const apply = document.createElement("button");
		apply.type = "button";
		apply.className = "odo-button__primary";
		apply.textContent = this.t("apply");
		const remove = document.createElement("button");
		remove.type = "button";
		remove.className = "odo-button__alt";
		remove.textContent = this.t("link_remove");
		actions.append(apply, remove);
		form.append(input, actions);
		const close = this.floating(belowOf(event.currentTarget as Element), form);
		remove.addEventListener("click", () => {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			close();
		});
		const submit = () => {
			const href = input.value.trim();
			const chain = editor.chain().focus();
			if (!href) chain.extendMarkRange("link").unsetLink().run();
			else if (editor.state.selection.empty && !editor.isActive("link"))
				chain.insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] }).run();
			else chain.extendMarkRange("link").setLink({ href }).run();
			close();
		};
		apply.addEventListener("click", submit);
		input.addEventListener("keydown", (key) => {
			if (key.key !== "Enter") return;
			key.preventDefault();
			submit();
		});
		input.focus();
	}

	closePanel() {
		if (!this.hasPanelTarget) return;
		this.panelBodyTarget.replaceChildren();
		this.panelTarget.classList.add("hidden");
		this.editor?.commands.focus();
	}

	jump(event: Event) {
		event.preventDefault();
		const editor = this.editor;
		const pos = Number((event.currentTarget as HTMLElement).dataset.pos);
		if (!editor) return;
		editor
			.chain()
			.setTextSelection(pos + 1)
			.run();
		(editor.view.nodeDOM(pos) as HTMLElement | null)?.scrollIntoView?.({ block: "start" });
	}

	private t = (key: string) => this.labelsValue[key] ?? key;

	private heading = (level: number) => this.t("block_heading").replace("%{level}", String(level));

	private editorContext(): EditorContext {
		return {
			manager: this.manager,
			previews: this.previews as Previews,
			t: this.t,
			near: (element, run) => {
				if (this.drawing) return this.nearby.watch(element, run);
				run();
				return () => undefined;
			},
			openPanel: (title, build) => this.openPanel(title, build),
			search: (tipo, query) => this.search(tipo, query),
			monsterFormats: this.monsterFormatsValue,
			openPaths: this.openPathsValue,
		};
	}

	private pickerContext(): PickerContext {
		return { ...this.editorContext(), editor: this.editor as Editor };
	}

	private groups(): MenuGroup[] {
		return insertGroups(this.pickerContext());
	}

	private async search(tipo: string, query: string): Promise<Reference[]> {
		const params = new URLSearchParams({ tipo, q: query, livro: this.bookValue });
		if (this.chapterValue) params.set("capitulo", this.chapterValue);
		const response = await fetch(`${this.referencesUrlValue}?${params}`, { headers: { Accept: "application/json" } });
		if (!response.ok) throw new Error(`references ${response.status}`);
		return ((await response.json()) as { results: Reference[] }).results;
	}

	// Parses `text` into a fresh editor. False, with the source view kept, when the text is not a document the
	// serializer reads back as itself: nothing the editor cannot hold is ever written over the chapter.
	private load(text: string): boolean {
		this.#teardown();
		try {
			const doc = this.manager.parse(text);
			this.drawing = true;
			const editor = this.create(doc);
			this.drawing = false;
			if (this.manager.serialize(editor.getJSON()) === this.manager.serialize(doc)) {
				this.editor = editor;
				this.touched = false;
				this.refresh();
				return true;
			}
			editor.destroy();
		} catch {
			this.drawing = false;
			this.hostTarget.replaceChildren();
		}
		this.notify("parse_failed");
		return false;
	}

	private create(doc: JSONContent): Editor {
		return new Editor({
			element: this.hostTarget,
			extensions: editorExtensions(this.editorContext()),
			content: doc,
			editorProps: {
				attributes: { class: "odo-markdown odo-chapter-editor", "aria-label": this.t("editor") },
				handleKeyDown: (view, event) => guardTests(this.editorContext(), view, event) || this.slash(event),
			},
			onUpdate: () => {
				this.touched = true;
				this.#changed();
			},
			onTransaction: () => this.refresh(),
		});
	}

	private slash(event: KeyboardEvent): boolean {
		const editor = this.editor;
		if (!editor || event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey) return false;
		const { empty, $from, from } = editor.state.selection;
		if (!empty || $from.parent.type.name !== "paragraph" || $from.parent.content.size > 0) return false;
		event.preventDefault();
		const caret = editor.view.coordsAtPos(from);
		this.menu({ left: caret.left, top: caret.bottom + 4 }, this.groups());
		return true;
	}

	#write(): boolean {
		try {
			this.fieldTarget.value = this.manager.serialize((this.editor as Editor).getJSON());
			return true;
		} catch {
			this.notify("serialize_failed");
			return false;
		}
	}

	private show(source: boolean) {
		this.inSource = source;
		if (source) {
			this.shown = this.fieldTarget.value;
			this.closePanel();
		}
		this.fieldTarget.classList.toggle("hidden", !source);
		for (const part of this.visualTargets) part.classList.toggle("hidden", source);
		for (const part of this.railTargets) part.classList.toggle("invisible", source);
		if (this.hasSourceButtonTarget) this.sourceButtonTarget.setAttribute("aria-pressed", String(source));
	}

	private notify(key?: string) {
		if (!this.hasNoticeTarget) return;
		this.noticeTarget.textContent = key ? this.t(key) : "";
		this.noticeTarget.classList.toggle("hidden", !key);
	}

	// Typing in a panel's form is not a change to the chapter until the piece is inserted.
	#changed = (event?: Event) => {
		if (event && this.hasPanelTarget && this.panelTarget.contains(event.target as Node)) return;
		this.modified = true;
		if (!this.hasSavedTarget) return;
		this.savedTarget.classList.add("hidden");
		this.unsavedTarget.classList.remove("hidden");
	};

	private refresh() {
		const editor = this.editor;
		if (!editor) return;
		for (const tool of this.toolTargets) tool.setAttribute("aria-pressed", String(editor.isActive(tool.dataset.tool ?? "")));
		if (this.hasBlockTypeTarget)
			this.blockTypeTarget.textContent = editor.isActive("heading") ? this.heading(Number(editor.getAttributes("heading").level)) : this.t("block_paragraph");
		this.sections();
		this.atoms();
		this.tableTools();
	}

	// The headings of the document, with the one the caret is under marked.
	private sections() {
		if (!this.hasSectionsTarget || !this.editor) return;
		const { doc, selection } = this.editor.state;
		const entries: { pos: number; level: number; text: string }[] = [];
		doc.descendants((node, pos) => {
			if (node.type.name === "heading") entries.push({ pos, level: node.attrs.level, text: node.textContent || "…" });
			return node.type.name === "doc";
		});
		const current = entries.filter((entry) => entry.pos < selection.from).at(-1);
		const top = Math.min(...entries.map((entry) => entry.level));
		this.sectionsTarget.replaceChildren(
			...entries.map((entry) => {
				const link = document.createElement("a");
				link.href = "#";
				link.dataset.pos = String(entry.pos);
				link.dataset.action = "chapter-editor#jump";
				link.className = "block py-0.5 pr-3 text-sm hover:underline aria-[current=true]:font-bold aria-[current=true]:text-link";
				link.style.paddingLeft = `${12 + (entry.level - top) * 14}px`;
				if (entry === current) link.setAttribute("aria-current", "true");
				link.textContent = entry.text;
				return link;
			}),
		);
	}

	// A selected piece of notation the editor does not edit in place offers what fits it (atom_bar.ts) and Remover.
	// The bar stays while the same piece is selected: the editor blurs, and so dispatches, when a press lands on the bar,
	// and a bar rebuilt between the press and the release would never get its click.
	private atoms() {
		const editor = this.editor;
		const { selection } = editor?.state ?? {};
		const piece = selection instanceof NodeSelection && ATOMS.has(selection.node.type.name) ? selection : undefined;
		if (this.pieceBar && piece && this.atomFor?.from === piece.from && this.atomFor.node.eq(piece.node)) return;
		this.#clearAtomBar();
		const dom = piece ? editor?.view.nodeDOM(piece.from) : undefined;
		if (!editor || !piece || !(dom instanceof HTMLElement)) return;
		const bar = document.createElement("div");
		bar.className = ATOM_BAR;
		// Where the piece ends can move once its preview arrives, so the bar follows its size.
		const place = () => {
			const rect = dom.getBoundingClientRect();
			const area = this.areaTarget.getBoundingClientRect();
			bar.style.top = `${rect.bottom - area.top + 4}px`;
			bar.style.left = `${Math.max(0, rect.left - area.left)}px`;
		};
		place();
		if (typeof ResizeObserver !== "undefined") {
			this.atomResize = new ResizeObserver(place);
			this.atomResize.observe(dom);
		}
		const remove = document.createElement("button");
		remove.type = "button";
		remove.className = "text-link underline hover:no-underline";
		remove.textContent = this.t("remove");
		remove.addEventListener("click", () => editor.chain().focus().deleteSelection().run());
		bar.append(...atomBar(this.pickerContext(), piece), remove);
		this.areaTarget.append(bar);
		this.pieceBar = bar;
		this.atomFor = { from: piece.from, node: piece.node };
	}

	// The table the caret is in offers its tools over its top right corner once the editor has the focus, and keeps
	// them while it is the same table, for the reason the atom bar does.
	private tableTools() {
		const editor = this.editor;
		const found = editor ? tableAround(editor.state.selection) : null;
		const dom = found ? editor?.view.nodeDOM(found.pos) : undefined;
		if (!editor || !found || !(dom instanceof HTMLElement)) {
			this.#clearTableBar();
			return;
		}
		if (this.tables?.dom !== dom) {
			this.#clearTableBar();
			if (!editor.view.hasFocus()) return;
			const bar = tableBar(editor, this.t, (at, groups) => this.menu(at, groups));
			this.areaTarget.append(bar.el);
			this.tables = { bar, dom };
			if (typeof ResizeObserver !== "undefined") {
				this.tables.resize = new ResizeObserver(() => this.#placeTableBar());
				this.tables.resize.observe(dom);
			}
		}
		this.tables.bar.sync();
		this.#placeTableBar();
	}

	#placeTableBar() {
		if (!this.tables) return;
		const { bar, dom } = this.tables;
		const rect = dom.getBoundingClientRect();
		const area = this.areaTarget.getBoundingClientRect();
		bar.el.style.top = `${rect.top - area.top - bar.el.offsetHeight - 2}px`;
		bar.el.style.left = `${Math.max(0, rect.right - area.left - bar.el.offsetWidth)}px`;
	}

	#clearTableBar() {
		this.tables?.resize?.disconnect();
		this.tables?.bar.el.remove();
		this.tables = undefined;
	}

	#clearAtomBar() {
		this.atomResize?.disconnect();
		this.atomResize = undefined;
		this.pieceBar?.remove();
		this.pieceBar = undefined;
		this.atomFor = undefined;
	}

	private openPanel(title: string, build: (close: () => void) => HTMLElement) {
		if (!this.hasPanelTarget) return;
		this.panelTitleTarget.textContent = title;
		this.panelBodyTarget.replaceChildren(build(() => this.closePanel()));
		this.panelBodyTarget.onkeydown = (event) => {
			if (event.key === "Escape") {
				this.closePanel();
				return;
			}
			if (event.key !== "Enter" || !(event.target instanceof HTMLInputElement)) return;
			event.preventDefault();
			this.panelBodyTarget.querySelector<HTMLElement>(".odo-button__primary")?.click();
		};
		this.panelTarget.classList.remove("hidden");
		this.panelBodyTarget.querySelector<HTMLElement>("input, select, button")?.focus();
	}

	private floating(at: { left: number; top: number }, content: HTMLElement) {
		this.closeFloating?.();
		this.closeFloating = openFloating(document.body, at, content, () => this.editor?.commands.focus());
		return this.closeFloating;
	}

	private menu(at: { left: number; top: number }, groups: MenuGroup[]) {
		this.closeFloating?.();
		this.closeFloating = openMenu(document.body, at, groups, () => this.editor?.commands.focus());
	}
}
